type SocialMediaProps = {
  facebook?: string;
  instagram?: string;
  whatsapp?: string; // puede ser número "529991234567" o un link completo
  maps?: string;
  position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
  strategy?: "fixed" | "absolute";
  className?: string;
};

const POS: Record<NonNullable<SocialMediaProps["position"]>, string> = {
  "bottom-right": "bottom-4 right-4",
  "bottom-left": "bottom-4 left-4",
  "top-right": "top-4 right-4",
  "top-left": "top-4 left-4",
};

// Normaliza los links según la red
function normalizeLink(type: "facebook" | "instagram" | "whatsapp" | "maps", value?: string) {
  if (!value) return undefined;
  let link = value.trim();

  // agregar https:// si falta
  if (!/^https?:\/\//i.test(link)) {
    link = "https://" + link;
  }

  if (type === "whatsapp") {
    // si es un número simple
    const number = link.replace(/\D/g, "");
    if (/^\d{8,15}$/.test(number)) {
      return `https://wa.me/${number}`;
    }
    // si ya es wa.me o api.whatsapp lo usamos directo
    if (link.includes("wa.me") || link.includes("whatsapp")) {
      return link;
    }
    // fallback → abrir como estaba
    return link;
  }

  return link;
}

export default function SocialMedia({
  facebook,
  instagram,
  whatsapp,
  maps,
  position = "bottom-right",
  strategy = "fixed",
  className,
}: SocialMediaProps) {
  const items = [
    {
      href: normalizeLink("facebook", facebook),
      label: "Facebook",
      bg: "bg-[#1877F2]",
      svg: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path
            d="M22 12.06C22 6.477 17.523 2 11.94 2 6.357 2 1.88 6.477 1.88 12.06c0 4.997 3.657 9.141 8.438 9.94v-7.03H7.897v-2.91h2.42V9.845c0-2.39 1.425-3.71 3.607-3.71 1.045 0 2.139.187 2.139.187v2.35h-1.205c-1.188 0-1.558.737-1.558 1.49v1.793h2.65l-.424 2.91h-2.226v7.03C18.343 21.2 22 17.056 22 12.06Z"
            fill="currentColor"
          />
        </svg>
      ),
    },
    {
      href: normalizeLink("instagram", instagram),
      label: "Instagram",
      bg: "bg-[#E4405F]",
      svg: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="2" />
          <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="2" />
          <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
        </svg>
      ),
    },
    {
      href: normalizeLink("whatsapp", whatsapp),
      label: "WhatsApp",
      bg: "bg-[#25D366]",
      svg: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path
            d="M20.5 12.2c0 4.66-3.83 8.44-8.56 8.44-1.5 0-2.91-.39-4.12-1.08L3.5 21l1.5-4.19a8.3 8.3 0 0 1-1.13-4.02C3.87 8.13 7.7 4.35 12.44 4.35c4.73 0 8.56 3.78 8.56 7.85Z"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d="M9.2 8.8c-.2.7-.66 2.28 1.48 4.28 2.14 2 3.7 1.53 4.4 1.33l.3-.1c.25-.1.55.03.68.28l.5.96c.14.27.06.6-.18.78-.44.33-1.3.82-2.33.82-1.85 0-4.22-1.07-5.9-2.7-1.68-1.63-2.8-3.93-2.8-5.7 0-1.02.48-1.9.8-2.34.18-.24.5-.31.77-.17l.97.5c.26.13.39.43.3.68l-.06.28Z"
            fill="currentColor"
          />
        </svg>
      ),
    },
    {
      href: normalizeLink("maps", maps),
      label: "Maps",
      bg: "bg-[#34A853]",
      svg: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 21s7-6.07 7-11A7 7 0 0 0 5 10c0 4.93 7 11 7 11Z"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
          />
          <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="2" />
        </svg>
      ),
    },
  ].filter((item) => Boolean(item.href));

  if (items.length === 0) return null;

  const strategyClass = strategy === "absolute" ? "absolute" : "fixed";

  return (
    <div
      className={`${strategyClass} ${POS[position]} z-[10000] ${className ?? ""}`}
      style={{ pointerEvents: "none" }}
    >
      <ul className="flex flex-col gap-3">
        {items.map(({ href, label, svg, bg }) => (
          <li key={label} style={{ pointerEvents: "auto" }}>
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              title={label}
              className={`flex h-12 w-12 items-center justify-center rounded-full shadow-lg ${bg} text-white transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/70`}
            >
              {svg}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
